import {
  BUILTIN_WRITING_SKILLS as BUILTINS,
  COMPANY_VOICE,
  type BuiltinWritingSkillKey,
} from '@/containers/sequences/clu/clu-skills.util';
import { writingSkillChoices } from '@/containers/sequences/clu/clu-writing.util';
import { plural } from '@/lib/formatters';
import type { EngageSequencingSettings } from '@/store/features/engage.feature';
import type { AgentEstimateBreakdown, AgentPrices } from '@/types';
import type { SkillDto } from '@/types/gtm-agents';

/** The Skills tab's views: every skill, or the ones Clu writes with. */
export const SKILLS_VIEWS = ['all', 'writing'] as const;
export type SkillsView = (typeof SKILLS_VIEWS)[number];

/** The view the tab shows: the one picked, else the URL's, else All. */
export function skillsViewOf(
  picked: SkillsView | null | undefined,
  param: SkillsView | undefined
): SkillsView {
  return picked ?? param ?? 'all';
}

/** The prebuilt writing skills a Clu step turns on, in the API's order. */
export const BUILTIN_WRITING_SKILLS: readonly BuiltinWritingSkill[] =
  BUILTINS.map((skill) => skill.key);
export type BuiltinWritingSkill = BuiltinWritingSkillKey;

/** Company voice is always on, so a step never names it. */
export type PrebuiltWritingSkill = 'voice' | BuiltinWritingSkill;

export const PREBUILT_WRITING_NAME = {
  voice: COMPANY_VOICE.name,
  ...Object.fromEntries(BUILTINS.map((skill) => [skill.key, skill.name])),
} as Record<PrebuiltWritingSkill, string>;

/** What the two prebuilt sequencing skills tell Clu. */
export const PREBUILT_WRITING_BRIEF = {
  pain_question:
    'Ask one yes or no question about a problem people in their role have.',
  close_the_loop: 'Close politely as the last email.',
} as const;

export interface PrebuiltWritingRow {
  key: PrebuiltWritingSkill;
  name: string;
  /** Its state, from what Clu reads when it writes. */
  line: string | null;
  /** Research's credits per lead; null until it is priced. */
  price?: number | null;
  /** How Research's price is estimated; null when the API sends none. */
  estimate?: AgentEstimateBreakdown | null;
  /** Clu email steps that use it; Company voice reads every Clu email. */
  steps: number | 'all';
}

/**
 * What Research on one lead costs Clu: the research agent on both scopes,
 * as a run charges it. Null until priced.
 */
export function cluResearchPrice(
  prices: AgentPrices | undefined
): number | null {
  return prices ? prices.research.both : null;
}

/**
 * How Research on one lead is estimated: the research agent on both scopes,
 * whose credits are `cluResearchPrice`. Null when the API sends none.
 */
export function cluResearchEstimate(
  prices: AgentPrices | undefined
): AgentEstimateBreakdown | null {
  return prices?.researchEstimate?.both ?? null;
}

const joined = (parts: (string | false)[]) => parts.filter(Boolean).join(' · ');

function voiceLine(
  settings: EngageSequencingSettings,
  notes: number | null | undefined
): string {
  const samples = settings.clu_voice_samples.length;
  const phrases = settings.clu_banned_phrases.length;
  const voice =
    joined([
      samples > 0 && plural(samples, 'sample email'),
      phrases > 0 && plural(phrases, 'banned phrase'),
    ]) || (settings.clu_style.trim() ? 'Style line' : '');
  // Company notes reach every Clu email too.
  return (
    joined([voice, !!notes && plural(notes, 'company note')]) || 'Not set up'
  );
}

function productLine(settings: EngageSequencingSettings): string {
  const details = settings.clu_content.product_details;
  const docs = settings.clu_content.documents ?? 0;
  const knowledge = plural(docs, 'Knowledge doc');
  if (details > 0 && docs > 0) return 'From Profile and ' + knowledge;
  if (details > 0) return 'From Profile';
  if (docs > 0) return 'From ' + knowledge;
  return 'No product details';
}

function proofLine(settings: EngageSequencingSettings): string {
  const { customer_proof: proof, case_studies: cases } = settings.clu_content;
  return (
    joined([
      proof > 0 && plural(proof, 'customer result'),
      cases > 0 && plural(cases, 'case study', 'case studies'),
    ]) || 'No customer proof'
  );
}

/**
 * The Prebuilt group: Company voice, then each builtin in the API's order,
 * with its state and how many Clu steps use it. `crm` is the connected
 * CRM's name, null with none, undefined while the CRMs load. `companyNotes`
 * is how many company notes Memory holds, when it is known.
 */
export function prebuiltWritingRows({
  settings,
  researchPrice,
  researchEstimate = null,
  crm,
  companyNotes,
}: {
  settings: EngageSequencingSettings;
  researchPrice: number | null;
  researchEstimate?: AgentEstimateBreakdown | null;
  crm: string | null | undefined;
  companyNotes?: number | null;
}): PrebuiltWritingRow[] {
  const steps = (key: BuiltinWritingSkill) =>
    settings.clu_skill_steps?.[key] ?? 0;
  const lines: Record<BuiltinWritingSkill, string | null> = {
    product: productLine(settings),
    proof: proofLine(settings),
    crm: crm === undefined ? null : crm ? crm + ' · free' : 'No CRM connected',
    research: null,
    pain_question: PREBUILT_WRITING_BRIEF.pain_question,
    close_the_loop: PREBUILT_WRITING_BRIEF.close_the_loop,
  };
  return [
    {
      key: 'voice',
      name: PREBUILT_WRITING_NAME.voice,
      line: voiceLine(settings, companyNotes),
      steps: 'all',
    },
    ...BUILTIN_WRITING_SKILLS.map((key) => ({
      key,
      name: PREBUILT_WRITING_NAME[key],
      line: lines[key],
      ...(key === 'research' && {
        price: researchPrice,
        estimate: researchEstimate,
      }),
      steps: steps(key),
    })),
  ];
}

/**
 * The Yours group: the library's live procedure skills made for writing, or
 * already on a Clu step.
 */
export function yourWritingSkills(
  skills: readonly SkillDto[],
  skillSteps: Record<string, number> | undefined
): SkillDto[] {
  return writingSkillChoices(skills).filter(
    (skill) => skill.forWriting || (skillSteps?.[skill.id] ?? 0) > 0
  );
}
